/*
 *  Student sidebar
 */

// dependencies
import React from "react";
import {
  Stack,
  Center,
  Text,
  Pressable,
  useTheme,
  ScrollView,
  Image,
  IconButton,
  Tooltip,
  HStack,
  Avatar,
  Box,
} from "native-base";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit,
  FiLogOut,
} from "react-icons/fi";

// logo
import logo from "../assets/logo-sm.png";

// redux
import { useSelector, useDispatch } from "react-redux";

import { setStudentDisplay } from "../redux/slices/NavigationSlice";
import { logoutMiddleware } from "../redux/middlewares/AuthenticationMiddleware";

import { LazyLoadComponent } from "react-lazy-load-image-component";

import { BsCalendarRangeFill } from "react-icons/bs";

export default function StudentSidebar(props) {
  const { menuData = [] } = props;
  const { colors, fontSizes } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { studentDisplay } = useSelector((state) => state.navigation);
  const user = useSelector((state) => state.authentication.user);
  const [avatarHovered, setAvatarHovered] = React.useState(false);

  return (
    <Stack bg="primary.500" h="100%">
      <Stack>
        <HStack px={5} py={4} alignItems="center" justifyContent={"flex-start"}>
          <Image
            source={logo}
            alt="logo"
            resizeMode="contain"
            h={7}
            w={7}
            tintColor={"white"}
          />

          <Text
            color="white"
            ml={3}
            fontSize="lg"
            display={[studentDisplay ?? "flex", studentDisplay ?? "flex"]}
          >
            Genuine
          </Text>
        </HStack>
        {/* user avatar */}
        <HStack
          px={4}
          mb={2}
          alignItems="center"
          justifyContent={"flex-start"}
          bg="secondary.50"
          py={5}
        >
          <Pressable
            onMouseEnter={() => {
              setAvatarHovered(true);
            }}
            onMouseLeave={() => {
              setAvatarHovered(false);
            }}
            onPress={() => {
              navigate("/student/profile/edit");
            }}
          >
            <LazyLoadComponent>
              <Avatar
                size={studentDisplay === "none" ? "sm" : "lg"}
                _text={{ color: "white" }}
                source={{
                  uri: user?.profilePicture ? user?.profilePicture : "",
                }}
              >
                {avatarHovered ? (
                  <FiEdit
                    color="white"
                    size={studentDisplay === "none" ? "20" : "35"}
                  />
                ) : (
                  // first leter of name
                  `${user?.firstname?.charAt(0).toUpperCase()}${user?.middlename
                    ?.charAt(0)
                    .toUpperCase()}${user?.lastname?.charAt(0).toUpperCase()}`
                )}
              </Avatar>
            </LazyLoadComponent>
          </Pressable>
          <Box>
            <Text
              color="white"
              ml={2}
              fontSize="sm"
              display={[studentDisplay ?? "flex", studentDisplay ?? "flex"]}
            >
              {user?.firstname} {user?.middlename}
            </Text>
            <Text
              color="white"
              ml={2}
              fontSize="sm"
              display={[studentDisplay ?? "flex", studentDisplay ?? "flex"]}
            >
              {user?.lastname}
            </Text>
          </Box>
        </HStack>
      </Stack>
      <ScrollView>
        {menuData.map((item, index) => (
          <Tooltip
            placement="right"
            offset={100}
            left={5}
            label={item.name}
            bg="primary.500"
            display={[
              studentDisplay === "none" ? "flex" : "none",
              studentDisplay === "none" ? "flex" : "none",
            ]}
          >
            <NavLink
              key={index}
              to={item.link}
              style={({ isActive }) => {
                return {
                  display: "block",
                  color: isActive ? colors["primary"][500] : "white",
                  fontFamily: "Montserrat Alternates",
                  fontSize: fontSizes["sm"],
                  textDecoration: "none",
                  backgroundColor: isActive ? colors["blue"][300] : "",
                  margin: ".5rem 1rem",
                  borderRadius: "5px",
                };
              }}
            >
              <Pressable
                rounded={"md"}
                px={".5rem"}
                py={".5rem"}
                w="100%"
                _hover={{
                  backgroundColor: "blue.300",
                }}
                flexDir="row"
                alignItems={"center"}
              >
                {item.icon}{" "}
                <Stack
                  display={[studentDisplay ?? "flex", studentDisplay ?? "flex"]}
                  ml={5}
                >
                  {item.name}
                </Stack>
              </Pressable>
            </NavLink>
          </Tooltip>
        ))}
        {/* Calendar */}
        <Tooltip
          placement="right"
          offset={100}
          left={5}
          label="Calendar"
          bg="primary.500"
          display={[
            studentDisplay === "none" ? "flex" : "none",
            studentDisplay === "none" ? "flex" : "none",
          ]}
        >
          <div
            style={{
              margin: ".5rem 1rem",
            }}
          >
            <Pressable
              rounded={"md"}
              px={".5rem"}
              py={".5rem"}
              w="100%"
              _hover={{
                backgroundColor: "blue.300",
              }}
              flexDir="row"
              alignItems={"center"}
              onPress={() => {
                window.open(
                  "https://calendar.google.com/calendar/u/1?cid=Y183OTRtZmpsNWthYWF0Z200MWduZW8wMjltb0Bncm91cC5jYWxlbmRhci5nb29nbGUuY29t"
                );
              }}
            >
              <BsCalendarRangeFill color="white" />
              <Stack
                display={[studentDisplay ?? "flex", studentDisplay ?? "flex"]}
                ml={5}
              >
                <Text color="white">Calendar</Text>
              </Stack>
            </Pressable>
          </div>
        </Tooltip>
      </ScrollView>

      {/* logout */}
      <Box px={5} mb={5}>
        <Tooltip
          placement="right"
          offset={100}
          left={5}
          label={"logout"}
          bg="red.500"
          display={[
            studentDisplay === "none" ? "flex" : "none",
            studentDisplay === "none" ? "flex" : "none",
          ]}
        >
          <Pressable
            rounded={"md"}
            px={".5rem"}
            py={".5rem"}
            w="100%"
            bg="red.500"
            flexDir="row"
            alignItems={"center"}
            justifyContent="center"
            _pressed={{
              opacity: 0.5,
            }}
            onPress={() => dispatch(logoutMiddleware())}
          >
            <FiLogOut color="white" size={20} />
            <Stack>
              <Text
                color="white"
                ml={2}
                display={[studentDisplay ?? "flex", studentDisplay ?? "flex"]}
              >
                Logout
              </Text>
            </Stack>
          </Pressable>
        </Tooltip>
      </Box>
      <Center>
        <IconButton
          icon={
            studentDisplay === "none" ? (
              <FiChevronRight size={30} color="white" />
            ) : (
              <FiChevronLeft size={30} color="white" />
            )
          }
          onPress={() =>
            dispatch(
              setStudentDisplay(studentDisplay === "none" ? "block" : "none")
            )
          }
        />
      </Center>
    </Stack>
  );
}
